import {
  useGetMeetingsQuery,
  useLazyGetMeetingTranscriptsQuery,
  useProjectId,
} from '@consigli/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMsalAuth } from './use-msal-auth';
import { parseTranscriptSegments, sortTranscriptsByCreation } from '../utils/transcript-utils';

import type { TranscriptSourceMeeting } from '../components/transcript-source-modal';
import type { ParsedTranscriptEntry } from '../utils/export-types';

const EMPTY_ENTRIES: ParsedTranscriptEntry[] = [];

export interface TranscriptSourceRef {
  meetingId?: string;
  sourceSegmentIds?: string[] | null;
  meetingTitle?: string | null;
  meetingScheduledStartTime?: string | null;
}

export interface UseTranscriptSourceResult {
  sourceItem: TranscriptSourceRef | null;
  segmentIds: string[];
  entries: ParsedTranscriptEntry[];
  meeting: TranscriptSourceMeeting | undefined;
  isLoading: boolean;
  hasError: boolean;
  showSource: (item: TranscriptSourceRef) => void;
  closeSource: () => void;
  retry: () => void;
}

export const useTranscriptSource = (): UseTranscriptSourceResult => {
  const projectId = useProjectId();
  const { acquireToken, userEmail, userId } = useMsalAuth();
  const [getTranscripts] = useLazyGetMeetingTranscriptsQuery();

  const [sourceItem, setSourceItem] = useState<TranscriptSourceRef | null>(null);
  const [entriesByMeeting, setEntriesByMeeting] = useState<Record<string, ParsedTranscriptEntry[]>>(
    {},
  );

  const [failedMeetingIds, setFailedMeetingIds] = useState<Record<string, true>>({});
  const [loadingMeetingId, setLoadingMeetingId] = useState<string | null>(null);
  const inFlightRef = useRef<string | null>(null);

  const {
    data: meetingsResponse,
    isLoading: isMeetingsLoading,
    isUninitialized: isMeetingsUninitialized,
  } = useGetMeetingsQuery({ projectId }, { skip: !projectId });

  const meetingsById = useMemo(() => {
    const meetings = Array.isArray(meetingsResponse)
      ? meetingsResponse
      : (meetingsResponse?.meetings ?? []);
    return new Map(meetings.map((meeting) => [meeting.id, meeting]));
  }, [meetingsResponse]);

  const meetingIdOf = (item: TranscriptSourceRef): string | undefined => item.meetingId;

  const activeMeetingId = sourceItem ? meetingIdOf(sourceItem) : undefined;
  const activeMeeting = activeMeetingId ? meetingsById.get(activeMeetingId) : undefined;

  const loadTranscript = useCallback(
    async (meetingId: string, joinUrl: string, calendarEventId?: string) => {
      inFlightRef.current = meetingId;
      setLoadingMeetingId(meetingId);
      try {
        const msAccessToken = await acquireToken();
        if (!msAccessToken) throw new Error('Failed to acquire MS token');

        const result = await getTranscripts({
          meetingId,
          projectId,
          msAccessToken,
          userEmail,
          userId,
          joinUrl,
          calendarEventId,
        }).unwrap();

        const transcripts = result.transcripts?.transcripts ?? [];
        setEntriesByMeeting((prev) => ({
          ...prev,
          [meetingId]: parseTranscriptSegments(sortTranscriptsByCreation(transcripts)),
        }));
      } catch (error) {
        console.error(`Failed to load transcript for meeting ${meetingId}:`, error);
        setFailedMeetingIds((prev) => ({ ...prev, [meetingId]: true }));
      } finally {
        inFlightRef.current = null;
        setLoadingMeetingId((current) => (current === meetingId ? null : current));
      }
    },
    [acquireToken, getTranscripts, projectId, userEmail, userId],
  );

  useEffect(() => {
    if (!activeMeetingId) return;
    if (entriesByMeeting[activeMeetingId] || failedMeetingIds[activeMeetingId]) return;
    if (inFlightRef.current) return;

    if (!activeMeeting) {
      if (!isMeetingsLoading && !isMeetingsUninitialized) {
        console.error(`Meeting ${activeMeetingId} not found in the project's meetings`);
        setFailedMeetingIds((prev) => ({ ...prev, [activeMeetingId]: true }));
      }
      return;
    }

    if (!activeMeeting.joinUrl) {
      console.error(`Meeting ${activeMeetingId} has no join url — transcripts unavailable`);
      setFailedMeetingIds((prev) => ({ ...prev, [activeMeetingId]: true }));
      return;
    }

    void loadTranscript(
      activeMeetingId,
      activeMeeting.joinUrl,
      activeMeeting.calendarEventId ?? undefined,
    );
  }, [
    activeMeeting,
    activeMeetingId,
    entriesByMeeting,
    failedMeetingIds,
    isMeetingsLoading,
    isMeetingsUninitialized,
    loadTranscript,
  ]);

  const clearFailure = useCallback((meetingId: string) => {
    setFailedMeetingIds((prev) => {
      if (!prev[meetingId]) return prev;
      const next = { ...prev };
      delete next[meetingId];
      return next;
    });
  }, []);

  const retry = useCallback(() => {
    if (activeMeetingId) clearFailure(activeMeetingId);
  }, [activeMeetingId, clearFailure]);

  const showSource = useCallback(
    (item: TranscriptSourceRef) => {
      const meetingId = meetingIdOf(item);
      if (meetingId) clearFailure(meetingId);
      setSourceItem(item);
    },
    [clearFailure],
  );

  const itemMeeting: TranscriptSourceMeeting | undefined =
    sourceItem && (sourceItem.meetingTitle || sourceItem.meetingScheduledStartTime)
      ? {
          title: sourceItem.meetingTitle,
          scheduledStartTime: sourceItem.meetingScheduledStartTime,
        }
      : undefined;

  const isWaitingForMeetings =
    !!activeMeetingId && !activeMeeting && (isMeetingsLoading || isMeetingsUninitialized);

  return {
    sourceItem,
    segmentIds: sourceItem?.sourceSegmentIds ?? [],
    entries: (activeMeetingId ? entriesByMeeting[activeMeetingId] : undefined) ?? EMPTY_ENTRIES,
    meeting: activeMeeting
      ? {
          title: activeMeeting.title,
          scheduledStartTime: activeMeeting.scheduledStartTime,
          scheduledEndTime: activeMeeting.scheduledEndTime,
          location: activeMeeting.location,
        }
      : itemMeeting,
    isLoading: (!!activeMeetingId && loadingMeetingId === activeMeetingId) || isWaitingForMeetings,
    hasError: !!activeMeetingId && !!failedMeetingIds[activeMeetingId],
    showSource,
    closeSource: () => setSourceItem(null),
    retry,
  };
};
